import {
  ElementType,
  Planet,
  PlanetSkill,
  PlanetSkillType,
  SkillFilterMode
} from '@apeiron/planet'
import { Box, styled, Typography } from '@mui/material'
import { PlanetDetailSectionTitle } from '@src/components/planet/detail/PlanetDetailSectionTitle'
import PlanetSkillItem from '@src/components/planet/share/planetSkill/PlanetSkillItem'
import { usePlanetSkillsContext } from '@src/contexts/planet/PlanetSkillsContext'
import { constructSkillUniqueKeyByValues } from '@src/util/apeiron/planet'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'

const MainContainer = styled(Box)`
  background: #333536;
  margin-bottom: 4px;
  color: #c4c4c4;
`

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  ${props => props.theme.breakpoints.up('res1024')} {
    padding: 15px 17px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 16px 17px;
  }
`

const TitleContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
`

const Remark = styled(Typography)`
  color: red;
  font-size: 14px;
  font-weight: 300;
  margin-left: 20px;
`

const SkillItem = styled(PlanetSkillItem)`
  .Custom-Cover {
    width: 80px;
    margin-left: 30px;
    margin-right: 30px;
  }
  .Custom-Info-Box {
    gap: 5px;
  }
  .Custom-Element-Name {
    font-size: 14px;
    line-height: 14px;
    margin-bottom: 6px;
  }
  .Custom-Name-Box {
    .Custom-Name {
      font-size: 24px;
      line-height: 32px;
    }
  }
  .Custom-Description {
    font-size: 14px;
    line-height: 22px;
  }
`

const PlanetSkillsSection: FC<Props> = (props: Props) => {
  const { planet, remark, title, types } = props

  const { searchSkills } = usePlanetSkillsContext()

  const handleOnMergeSkill = useCallback(
    (skills: PlanetSkill[]): PlanetSkill[] => {
      const first = R.propOr(null, '0', skills) as PlanetSkill | null

      const formattedDescription = R.join(
        '<br/>',
        R.pluck('description', skills)
      )

      const merged: PlanetSkill | null = first
        ? {
            attributes: {
              element: ElementType.None,
              mana: 0,
              planetClass: planet.planetClass,
              planetType: planet.planetType,
              requiredPoints: 0,
              weapon: 0
            },
            disabled: false,
            description: formattedDescription,
            descriptionShort: R.all(
              value => value === undefined,
              R.pluck('descriptionShort', skills)
            )
              ? ''
              : R.join('<br/>', R.pluck('descriptionShort', skills)),
            geneId: 0,
            image: first.image,
            name: '',
            skillId: first.skillId,
            skillUpgrade: first.skillUpgrade,
            uniqueKey: constructSkillUniqueKeyByValues(
              PlanetSkillType.Binded,
              planet.planetClass,
              0
            ),
            type: PlanetSkillType.Binded
          }
        : null

      return merged ? [merged] : []
    },
    [planet]
  )

  const filterMode = useMemo(() => {
    if (R.includes(PlanetSkillType.Active, types)) {
      return SkillFilterMode.Active
    } else if (R.includes(PlanetSkillType.Passive, types)) {
      return SkillFilterMode.Passive
    } else {
      return SkillFilterMode.Binded
    }
  }, [types])

  const skills = useMemo(() => {
    let searchResult: PlanetSkill[] = []

    if (planet) {
      R.forEach((type: PlanetSkillType) => {
        searchResult = R.concat(searchResult, searchSkills({ type, planet }))
      }, types)

      if (filterMode === SkillFilterMode.Binded) {
        searchResult = handleOnMergeSkill(searchResult)
      }
    }

    // Sort by CSkill or PSkill
    const sortedSkills = R.sort((a: PlanetSkill, b: PlanetSkill) => {
      if (filterMode === SkillFilterMode.Active) {
        return (
          (planet.cSkill?.indexOf(Number(a.geneId)) || 0) -
          (planet.cSkill?.indexOf(Number(b.geneId)) || 0)
        )
      } else if (filterMode === SkillFilterMode.Passive) {
        return (
          (planet.pSkill?.indexOf(Number(a.geneId)) || 0) -
          (planet.pSkill?.indexOf(Number(b.geneId)) || 0)
        )
      } else return 0
    }, searchResult)

    return sortedSkills
  }, [filterMode, handleOnMergeSkill, planet, searchSkills, types])

  return !R.isEmpty(skills) ? (
    <>
      <MainContainer>
        <TitleContainer>
          <PlanetDetailSectionTitle title={title} subTitle={title} />
          {remark && <Remark>{remark}</Remark>}
        </TitleContainer>
        <Container>
          {R.map(
            skill => (
              <SkillItem mode={filterMode} key={skill.skillId} skill={skill} />
            ),
            skills
          )}
        </Container>
      </MainContainer>
    </>
  ) : (
    <></>
  )
}

type Props = {
  planet: Planet
  remark?: string
  title: string
  types: PlanetSkillType[]
}

export default PlanetSkillsSection
